import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { type RegulationDraft, useRoleGroup, useSaveGroupRegulation } from '../api/regulations';
import { Avatar } from '../components/Avatar';
import { FineReasonsBlock } from '../components/FineReasonsBlock';
import { Icon } from '../components/Icon';
import {
  cleanDraft,
  draftFrom,
  formatDate,
  isEmptyDraft,
  RegulationEditor,
  RegulationView,
} from '../components/RegulationBlock';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { usePageTitle, usePersonDrawer } from '../layout/shellContext';

export function RoleGroupPage() {
  const { id } = useParams();
  const group = useRoleGroup(id);
  const save = useSaveGroupRegulation(id ?? '');
  const [draft, setDraft] = useState<RegulationDraft | null>(null);
  const { open } = usePersonDrawer();
  usePageTitle(group.data?.name ?? 'Группа должностей');

  if (group.isPending) return <LoadingState label="Загружаю группу" />;
  if (group.isError) return <ErrorState error={group.error} onRetry={() => void group.refetch()} />;
  const data = group.data;
  const signed = data.members.filter((member) => member.signedAt).length;

  return (
    <div className="page">
      <Link to="/regulations" className="link">
        ← Все группы
      </Link>
      <section className="panel regulation">
        <div className="panel-header">
          <h2>Регламент группы</h2>
          <div className="panel-actions">
            {draft ? (
              <>
                <button type="button" className="btn-secondary" onClick={() => setDraft(null)}>
                  Отмена
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={save.isPending || isEmptyDraft(draft)}
                  onClick={() => save.mutate(cleanDraft(draft), { onSuccess: () => setDraft(null) })}
                >
                  Сохранить для {data.members.length} сотрудников
                </button>
              </>
            ) : (
              <>
                {data.regulation ? (
                  <span className="reg-version">
                    Версия {data.regulation.version} от {formatDate(data.regulation.updatedAt)}
                  </span>
                ) : null}
                {data.canEdit ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setDraft(draftFrom(data.regulation))}
                  >
                    <Icon name="pen" size={14} />
                    Изменить
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
        {draft ? (
          <>
            <RegulationEditor draft={draft} onChange={setDraft} />
            {save.isError ? <ErrorState title="Регламент не сохранён" error={save.error} /> : null}
          </>
        ) : data.regulation ? (
          <RegulationView regulation={data.regulation} />
        ) : (
          <EmptyState title="Регламент ещё не заполнен" />
        )}
      </section>

      <FineReasonsBlock roleGroupId={data.id} membersCount={data.members.length} />

      <section className="panel">
        <div className="panel-header">
          <h2>Сотрудники · {data.members.length}</h2>
          <span className={`status ${signed === data.members.length ? 'status-ok' : 'status-info'}`}>
            Подписали {signed} из {data.members.length}
          </span>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Подпись</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((member) => (
                <tr key={member.id}>
                  <td>
                    <button type="button" className="person-line" onClick={() => open(member.id)}>
                      <Avatar name={member.fullName} photoUrl={member.photoUrl} />
                      <span className="person-line-text">
                        <b>{member.fullName}</b>
                        <small>
                          {member.position ?? ''}
                          {member.personal ? ' · личный регламент' : ''}
                        </small>
                      </span>
                    </button>
                  </td>
                  <td>
                    {!member.hasRegulation ? (
                      <span className="status status-neutral">Нет регламента</span>
                    ) : member.signedAt ? (
                      <span className="status status-ok">Подписан {formatDate(member.signedAt)}</span>
                    ) : (
                      <span className="status status-bad">Не подписан</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
